// Subscription management, Stripe payments, GTC, client registration API
import { api } from './client';

// ============================================================================
// SUBSCRIPTION TYPES
// ============================================================================

export interface SubscriptionPlan {
  id: number;
  name: string;
  slug: string;
  description?: string;
  // Localized names and descriptions
  name_en?: string;
  name_de?: string;
  name_fr?: string;
  name_it?: string;
  name_localized?: string;
  description_en?: string;
  description_de?: string;
  description_fr?: string;
  description_it?: string;
  description_localized?: string;
  // Pricing
  monthly_price_chf: number;
  yearly_price_chf: number;
  hybrid_monthly_price_chf?: number;
  hybrid_yearly_price_chf?: number;
  // Limits
  max_domains: number;
  max_external_targets: number;
  max_internal_targets: number;
  max_agents: number;
  min_scan_frequency_hours: number;
  manual_scans_per_day: number;
  manual_scans_per_week: number;
  manual_scans_per_month: number;
  max_projects?: number;
  max_additional_users?: number;
  data_retention_days?: number;
  max_pinned_scans?: number;
  // Features
  websocket_enabled: boolean;
  darknet_monitoring: boolean;
  endpoint_review_agent: boolean;
  custom_report_templates: boolean; // Allows client to customize report blocks, order, and styling
  // Stripe integration
  stripe_price_id_monthly?: string;
  stripe_price_id_yearly?: string;
  stripe_hybrid_price_id_monthly?: string;
  stripe_hybrid_price_id_yearly?: string;
  stripe_product_id?: string;
  stripe_hybrid_product_id?: string;
  // Status
  is_public?: boolean;
  is_active: boolean;
  is_hidden?: boolean;
  sort_order?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ClientSubscription {
  id: number;
  client_id: number;
  plan_id: number;
  plan?: SubscriptionPlan;
  billing_cycle: 'monthly' | 'yearly';
  hybrid_enabled: boolean;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'paused' | 'expired' | 'suspended' | 'pending_approval';
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  current_period_start?: string;
  current_period_end?: string;
  trial_start?: string;
  trial_end?: string;
  cancel_at_period_end?: boolean;
  canceled_at?: string;
  cancellation_reason?: string;
  last_payment_date?: string;
  last_payment_amount?: number;
  created_at: string;
  updated_at?: string;
}

export interface ClientPlanOverride {
  id: number;
  client_id: number;
  max_domains?: number;
  max_external_targets?: number;
  max_internal_targets?: number;
  min_scan_frequency_hours?: number;
  manual_scans_per_day?: number;
  manual_scans_per_week?: number;
  manual_scans_per_month?: number;
  max_projects?: number;
  max_additional_users?: number;
  max_agents?: number;
  websocket_enabled?: boolean;
  data_retention_days?: number;
  max_pinned_scans?: number;
  darknet_monitoring?: boolean;
  endpoint_review_agent?: boolean;
  custom_report_templates?: boolean;
  custom_monthly_price?: number;
  custom_yearly_price?: number;
  custom_plan_name?: string;
  notes?: string;
}

export interface EffectiveLimits {
  max_domains: number;
  max_external_targets: number;
  max_internal_targets: number;
  max_agents: number;
  min_scan_frequency_hours: number;
  manual_scans_per_day: number;
  manual_scans_per_week: number;
  manual_scans_per_month: number;
  max_projects: number;
  max_additional_users: number;
  data_retention_days: number;
  max_pinned_scans: number;
  websocket_enabled: boolean;
  darknet_monitoring: boolean;
  endpoint_review_agent: boolean;
  custom_report_templates: boolean;
  is_trial: boolean;
  trial_end?: string;
  trial_expired: boolean;
  plan_name?: string;
  plan_slug?: string;
}

export interface GTCVersion {
  id: number;
  version: string;
  content: string;
  changes_summary?: string;
  effective_date: string;
  is_active: boolean;
  is_draft: boolean;
}

export interface GTCAcceptance {
  required: boolean;
  accepted: boolean;
  gtc_version?: string;
  gtc_id?: number;
  accepted_at?: string;
}

export interface ClientRegistration {
  id: number;
  company_name: string;
  email: string;
  status: 'pending_verification' | 'pending_approval' | 'approved' | 'rejected';
  created_at: string;
  email_verified_at?: string;
  approved_at?: string;
  rejection_reason?: string;
  // IT Profile fields
  infrastructure_type?: string;
  it_management?: string;
  technical_expertise?: string;
  primary_security_concern?: string;
  industry_regulations?: string;
  team_size?: string;
}

export interface StripeConfig {
  enabled: boolean;
  mode: 'test' | 'live';
  public_key: string;
}

export interface CheckoutSession {
  session_id: string;
  url: string;
  public_key: string;
}

export interface SubscriptionStatus {
  has_subscription: boolean;
  subscription_id?: number;
  status?: string;
  billing_cycle?: string;
  hybrid_enabled?: boolean;
  current_period_start?: string;
  current_period_end?: string;
  is_trialing?: boolean;
  trial_days_remaining?: number;
  trial_end?: string;
  limits?: EffectiveLimits;
  usage?: {
    domains_used: number;
    external_targets_used: number;
    internal_targets_used: number;
    manual_scans_this_month: number;
    projects_used: number;
    users_used: number;
    agents_used: number;
  };
  plan?: {
    id: number;
    name: string;
    slug: string;
    description: string;
  };
  message?: string;
}

export interface PaymentHistoryItem {
  id: number;
  client_id: number;
  subscription_id: number;
  stripe_invoice_id: string;
  stripe_payment_id: string;
  amount: number;
  currency: string;
  status: string;
  payment_method: string;
  description: string;
  invoice_pdf_url: string;
  created_at: string;
}

// ============================================================================
// SUBSCRIPTION API
// ============================================================================

export const subscriptionAPI = {
  // Get all public subscription plans
  getPlans: (lang?: string) => {
    console.log('📋 Fetching subscription plans');
    return api.get<SubscriptionPlan[]>('/plans', { params: { lang } });
  },

  // Get client's current subscription
  getSubscription: () => {
    console.log('📋 Fetching client subscription');
    return api.get<ClientSubscription>('/subscription');
  },

  // Get effective limits (plan + overrides)
  getLimits: () => {
    console.log('📋 Fetching effective limits');
    return api.get<EffectiveLimits>('/subscription/limits');
  },

  // Get usage statistics
  getUsage: () => {
    console.log('📋 Fetching usage statistics');
    return api.get('/subscription/usage');
  },

  // Start checkout process
  startCheckout: (planSlug: string, billingCycle: 'monthly' | 'yearly') => {
    console.log('💳 Starting checkout for plan:', planSlug);
    return api.post('/subscriptions/checkout', { plan_slug: planSlug, billing_cycle: billingCycle });
  },

  // Cancel subscription
  cancelSubscription: () => {
    console.log('❌ Canceling subscription');
    return api.post('/subscriptions/cancel');
  },

  // Resume subscription
  resumeSubscription: () => {
    console.log('▶️ Resuming subscription');
    return api.post('/subscriptions/resume');
  },

  // Admin: Get all subscriptions
  adminGetAll: () => {
    console.log('🔧 Admin: Fetching all subscriptions');
    return api.get<ClientSubscription[]>('/admin/subscriptions');
  },

  // Admin: Update client subscription
  adminUpdate: (clientId: number, data: Partial<ClientSubscription>) => {
    console.log('🔧 Admin: Updating subscription for client:', clientId);
    return api.put(`/admin/subscriptions/${clientId}`, data);
  },

  // Admin: Get all plans (including hidden)
  adminGetPlans: () => {
    console.log('🔧 Admin: Fetching all plans');
    return api.get<SubscriptionPlan[]>('/admin/plans');
  },

  // Admin: Create plan
  adminCreatePlan: (plan: Partial<SubscriptionPlan>) => {
    console.log('🔧 Admin: Creating plan');
    return api.post<SubscriptionPlan>('/admin/plans', plan);
  },

  // Admin: Update plan
  adminUpdatePlan: (id: number, plan: Partial<SubscriptionPlan>) => {
    console.log('🔧 Admin: Updating plan:', id);
    return api.put<SubscriptionPlan>(`/admin/plans/${id}`, plan);
  },

  // Admin: Delete (deactivate) plan
  adminDeletePlan: (id: number) => {
    console.log('🔧 Admin: Deactivating plan:', id);
    return api.delete(`/admin/plans/${id}`);
  },

  // Admin: Get client subscription with overrides
  adminGetSubscription: (clientId: number) => {
    console.log('🔧 Admin: Getting subscription for client:', clientId);
    return api.get<{ subscription: ClientSubscription | null; overrides: ClientPlanOverride | null }>(`/admin/clients/${clientId}/subscription`);
  },

  // Admin: Set client subscription (basic - plan and status only)
  adminSetSubscription: (clientId: number, planId: number | null, status?: string) => {
    console.log('🔧 Admin: Setting subscription for client:', clientId);
    return api.put(`/admin/clients/${clientId}/subscription`, { plan_id: planId, status });
  },

  // Admin: Update client subscription (full update with all fields)
  adminUpdateSubscription: (clientId: number, data: {
    plan_id?: number;
    status?: string;
    billing_cycle?: string;
    trial_ends_at?: string;
    current_period_end?: string;
  }) => {
    console.log('🔧 Admin: Updating subscription for client:', clientId);
    return api.put(`/admin/clients/${clientId}/subscription`, data);
  },

  // Admin: Get client overrides
  adminGetOverrides: (clientId: number) => {
    console.log('🔧 Admin: Getting overrides for client:', clientId);
    return api.get(`/admin/clients/${clientId}/overrides`);
  },

  // Admin: Set client overrides
  adminSetOverrides: (clientId: number, overrides: Record<string, any>) => {
    console.log('🔧 Admin: Setting overrides for client:', clientId);
    return api.post(`/admin/clients/${clientId}/overrides`, overrides);
  },
};

// ============================================================================
// STRIPE API
// ============================================================================

export const stripeAPI = {
  // Get Stripe configuration (public key)
  getConfig: () => {
    console.log('💳 Fetching Stripe config');
    return api.get<StripeConfig>('/stripe/config');
  },

  // Get subscription status with limits and usage
  getSubscriptionStatus: () => {
    console.log('📋 Fetching subscription status');
    return api.get<SubscriptionStatus>('/subscription/status');
  },

  // Get payment history
  getPaymentHistory: () => {
    console.log('💰 Fetching payment history');
    return api.get<PaymentHistoryItem[]>('/subscription/payments');
  },

  // Create checkout session
  createCheckout: (planId: number, billingCycle: 'monthly' | 'yearly', hybridAddon?: boolean) => {
    console.log('💳 Creating checkout session for plan:', planId);
    return api.post<CheckoutSession>('/subscription/checkout', {
      plan_id: planId,
      billing_cycle: billingCycle,
      hybrid_addon: hybridAddon || false,
    });
  },

  // Verify checkout success
  verifyCheckout: (sessionId: string) => {
    console.log('✅ Verifying checkout:', sessionId);
    return api.get('/subscription/checkout/success', { params: { session_id: sessionId } });
  },

  // Open customer portal
  openPortal: () => {
    console.log('👤 Opening customer portal');
    return api.post<{ url: string }>('/subscription/portal');
  },

  // Cancel subscription
  cancelSubscription: (reason?: string) => {
    console.log('❌ Canceling subscription');
    return api.post('/subscription/cancel', { reason });
  },

  // Reactivate canceled subscription
  reactivateSubscription: () => {
    console.log('▶️ Reactivating subscription');
    return api.post('/subscription/reactivate');
  },

  // Upgrade/downgrade subscription
  upgradeSubscription: (planId: number, billingCycle: 'monthly' | 'yearly') => {
    console.log('⬆️ Upgrading subscription to plan:', planId);
    return api.post<{ message: string; new_plan: string; billing_cycle: string }>('/subscription/upgrade', {
      plan_id: planId,
      billing_cycle: billingCycle,
    });
  },
};

// ============================================================================
// GTC API
// ============================================================================

export const gtcAPI = {
  // Get active GTC version
  getActive: (lang?: string) => {
    console.log('📜 Fetching active GTC');
    return api.get<GTCVersion>('/gtc/active', { params: { lang } });
  },

  // Get specific GTC version
  getVersion: (id: number, lang?: string) => {
    console.log('📜 Fetching GTC version:', id);
    return api.get<GTCVersion>(`/gtc/${id}`, { params: { lang } });
  },

  // Accept GTC
  accept: (gtcVersionId: number) => {
    console.log('✅ Accepting GTC version:', gtcVersionId);
    return api.post('/gtc/accept', { gtc_version_id: gtcVersionId });
  },

  // Check GTC acceptance status
  checkAcceptance: () => {
    console.log('🔍 Checking GTC acceptance');
    return api.get<GTCAcceptance>('/gtc/check');
  },

  // Get user's GTC history
  getHistory: () => {
    console.log('📋 Fetching GTC history');
    return api.get('/gtc/history');
  },

  // Admin: Get all GTC versions
  adminGetAll: () => {
    console.log('🔧 Admin: Fetching all GTC versions');
    return api.get<GTCVersion[]>('/admin/gtc');
  },

  // Admin: Get GTC version with all languages
  adminGetVersion: (id: number) => {
    console.log('🔧 Admin: Fetching GTC version:', id);
    return api.get(`/admin/gtc/${id}`);
  },

  // Admin: Create GTC version
  adminCreate: (gtc: Record<string, any>) => {
    console.log('🔧 Admin: Creating GTC version');
    return api.post('/admin/gtc', gtc);
  },

  // Admin: Update GTC version
  adminUpdate: (id: number, gtc: Record<string, any>) => {
    console.log('🔧 Admin: Updating GTC version:', id);
    return api.put(`/admin/gtc/${id}`, gtc);
  },

  // Admin: Publish GTC version
  adminPublish: (id: number, data: { effective_date?: string; set_as_active?: boolean }) => {
    console.log('🔧 Admin: Publishing GTC version:', id);
    return api.post(`/admin/gtc/${id}/publish`, data);
  },

  // Admin: Activate GTC version
  adminActivate: (id: number) => {
    console.log('🔧 Admin: Activating GTC version:', id);
    return api.post(`/admin/gtc/${id}/activate`);
  },

  // Admin: Delete GTC version
  adminDelete: (id: number) => {
    console.log('🔧 Admin: Deleting GTC version:', id);
    return api.delete(`/admin/gtc/${id}`);
  },

  // Admin: Get client GTC history
  adminGetClientHistory: (clientId: number) => {
    console.log('🔧 Admin: Fetching GTC history for client:', clientId);
    return api.get(`/admin/gtc/clients/${clientId}/history`);
  },
};

// ============================================================================
// CLIENT REGISTRATION API
// ============================================================================

export const registrationAPI = {
  // Register new client (public endpoint)
  register: (data: {
    company_name: string;
    email: string;
    password: string;
    password_confirm: string;
    first_name: string;
    last_name: string;
    gtc_accepted: boolean;
    gtc_version_id: number;
    captcha_token?: string;
    phone?: string;
    address?: string;
    industry?: string;
    // IT Profile fields
    infrastructure_type?: string;
    it_management?: string;
    technical_expertise?: string;
    primary_security_concern?: string;
    industry_regulations?: string;
    team_size?: string;
  }) => {
    console.log('📝 Registering new client');
    return api.post('/client-register', data);
  },

  // Verify email
  verifyEmail: (token: string) => {
    console.log('✉️ Verifying email');
    return api.post('/client-register/verify', { token });
  },

  // Resend verification email
  resendVerification: (email: string) => {
    console.log('📧 Resending verification email');
    return api.post('/client-register/resend-verification', { email });
  },

  // Admin: Get pending registrations
  adminGetPending: () => {
    console.log('🔧 Admin: Fetching pending registrations');
    return api.get<ClientRegistration[]>('/admin/registrations');
  },

  // Admin: Get all registrations
  adminGetAll: (params?: { status?: string; page?: number; limit?: number }) => {
    console.log('🔧 Admin: Fetching all registrations');
    return api.get<ClientRegistration[]>('/admin/registrations/all', { params });
  },

  // Admin: Approve registration
  adminApprove: (id: number, data?: { plan_id?: number; notes?: string }) => {
    console.log('🔧 Admin: Approving registration:', id);
    return api.post(`/admin/registrations/${id}/approve`, data);
  },

  // Admin: Reject registration
  adminReject: (id: number, reason: string) => {
    console.log('🔧 Admin: Rejecting registration:', id);
    return api.post(`/admin/registrations/${id}/reject`, { reason });
  },

  // Admin: Manually verify email
  adminVerifyEmail: (id: number) => {
    console.log('🔧 Admin: Manually verifying email for registration:', id);
    return api.post(`/admin/registrations/${id}/verify-email`);
  },
};
